/**
 * Privacy Policy Page
 *
 * Displays the HoneyDo Privacy Policy with COPPA compliance information.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from 'flowbite-react';
import { ArrowLeft, Download, Printer } from 'lucide-react';
import EnhancedButton from '../components/ui/EnhancedButton';

const PrivacyPolicy: React.FC = () => {
  const navigate = useNavigate();
  const [privacyContent, setPrivacyContent] = useState<string>('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Load privacy policy markdown file
    fetch('/PRIVACY_POLICY.md')
      .then(response => response.text())
      .then(text => {
        setPrivacyContent(text);
        setLoading(false);
      })
      .catch(error => {
        console.error('Failed to load Privacy Policy:', error);
        setPrivacyContent('Privacy Policy content could not be loaded. Please contact support@honeydolist.vip');
        setLoading(false);
      });
  }, []);

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    const blob = new Blob([privacyContent], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'HoneyDo-Privacy-Policy.md';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Simple markdown to HTML converter
  const renderMarkdown = (markdown: string): React.ReactElement => {
    const lines = markdown.split('\n');
    const elements: React.ReactElement[] = [];
    let currentList: string[] = [];
    let listType: 'ul' | 'ol' | null = null;

    const flushList = (key: number) => {
      if (currentList.length > 0 && listType) {
        const ListTag = listType;
        elements.push(
          <ListTag key={`list-${key}`} className={`${listType === 'ul' ? 'list-disc' : 'list-decimal'} ml-6 mb-4 space-y-1`}>
            {currentList.map((item, idx) => (
              <li key={`item-${idx}`} className="text-gray-700">{item}</li>
            ))}
          </ListTag>
        );
        currentList = [];
        listType = null;
      }
    };

    lines.forEach((line, index) => {
      const trimmed = line.trim();

      // Skip empty lines
      if (!trimmed) {
        flushList(index);
        return;
      }

      // Headers
      if (trimmed.startsWith('# ')) {
        flushList(index);
        elements.push(
          <h1 key={index} className="text-3xl font-bold text-gray-900 mt-8 mb-4">
            {trimmed.substring(2)}
          </h1>
        );
      } else if (trimmed.startsWith('## ')) {
        flushList(index);
        elements.push(
          <h2 key={index} className="text-2xl font-bold text-gray-900 mt-8 mb-4 border-b border-gray-200 pb-2">
            {trimmed.substring(3)}
          </h2>
        );
      } else if (trimmed.startsWith('### ')) {
        flushList(index);
        elements.push(
          <h3 key={index} className="text-xl font-bold text-gray-800 mt-6 mb-3">
            {trimmed.substring(4)}
          </h3>
        );
      }
      // Horizontal rule
      else if (trimmed === '---') {
        flushList(index);
        elements.push(<hr key={index} className="border-t border-gray-300 my-8" />);
      }
      // Lists
      else if (trimmed.startsWith('- ')) {
        if (listType !== 'ul') {
          flushList(index);
          listType = 'ul';
        }
        currentList.push(trimmed.substring(2));
      } else if (/^\d+\. /.test(trimmed)) {
        if (listType !== 'ol') {
          flushList(index);
          listType = 'ol';
        }
        currentList.push(trimmed.replace(/^\d+\. /, ''));
      }
      // Paragraphs
      else {
        flushList(index);
        // Process inline markdown
        let content = trimmed;
        // Bold
        content = content.replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold text-gray-900">$1</strong>');
        // Links
        content = content.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-purple-600 hover:text-purple-700 underline">$1</a>');

        elements.push(
          <p key={index} className="mb-4 text-gray-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: content }} />
        );
      }
    });

    flushList(elements.length);
    return <>{elements}</>;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 py-12 px-4 print:bg-white">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8 print:hidden">
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Back to App</span>
            </button>
            <div className="flex gap-2">
              <button
                onClick={handlePrint}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Printer className="w-4 h-4" />
                <span className="text-sm font-medium">Print</span>
              </button>
              <button
                onClick={handleDownload}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                <Download className="w-4 h-4" />
                <span className="text-sm font-medium">Download</span>
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <Card className="shadow-2xl border-none print:shadow-none">
          <div className="p-8 print:p-0">
            {/* Logo and Title */}
            <div className="text-center mb-8 print:mb-6">
              <h1 className="text-4xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-2 print:text-gray-900">
                🍯 HoneyDo Privacy Policy
              </h1>
              <p className="text-gray-600 text-sm">COPPA Compliant Family Platform (13+)</p>
            </div>

            {/* Policy Content */}
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full mx-auto mb-4"></div>
                <p className="text-gray-600">Loading Privacy Policy...</p>
              </div>
            ) : (
              <div className="prose prose-sm max-w-none">
                {renderMarkdown(privacyContent)}
              </div>
            )}

            {/* Contact Info */}
            <div className="mt-8 pt-8 border-t border-gray-200 text-center print:mt-6 print:pt-6">
              <p className="text-sm text-gray-600 mb-2">
                Questions about our Privacy Policy?
              </p>
              <p className="text-sm">
                <a href="mailto:privacy@honeydolist.vip" className="text-purple-600 hover:text-purple-700 font-medium">
                  privacy@honeydolist.vip
                </a>
                {' • '}
                <a href="mailto:coppa@honeydolist.vip" className="text-purple-600 hover:text-purple-700 font-medium">
                  coppa@honeydolist.vip
                </a>
              </p>
            </div>
          </div>
        </Card>

        {/* Footer */}
        <div className="mt-8 text-center text-sm text-gray-500 print:hidden">
          <p>© 2025 HoneyDo • COPPA Compliant Family Platform</p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
